import { useEffect, useRef, useState } from "react";

const DURATION = 240;

/** Keep animation state above Recharts so rebuilding a sector cannot skip its transition. */
export const useDonutAnimation = (activeIndex: number | null, count: number, data: unknown, key: string) => {
	const [frame, setFrame] = useState(() => ({ data, key, growth: Array<number>(count).fill(0), presence: 0 }));
	const current = useRef(frame);
	useEffect(() => {
		const from = current.current.data === data && current.current.key === key && current.current.growth.length === count
			? current.current : { data, key, growth: Array<number>(count).fill(0), presence: 0 };
		const target = { data, key, growth: Array.from({ length: count }, (_, index) => index === activeIndex ? 1 : 0), presence: activeIndex === null ? 0 : 1 };
		const motion = window.matchMedia?.("(prefers-reduced-motion: reduce)");
		const update = (next: typeof frame) => { current.current = next; setFrame(next); };
		if (motion?.matches || from.presence === target.presence && from.growth.every((value, index) => value === target.growth[index])) {
			update(target);
			return;
		}
		const start = performance.now();
		let request = 0;
		const tick = (time: number) => {
			const progress = motion?.matches ? 1 : Math.min(1, Math.max(0, (time - start) / DURATION));
			const eased = 1 - (1 - progress) ** 3;
			update({ data, key,
				growth: from.growth.map((value, index) => value + (target.growth[index] - value) * eased),
				presence: from.presence + (target.presence - from.presence) * eased,
			});
			if (progress < 1) request = requestAnimationFrame(tick);
		};
		request = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(request);
	}, [activeIndex, count, data, key]);
	return frame.data === data && frame.key === key && frame.growth.length === count
		? frame : { growth: Array<number>(count).fill(0), presence: 0 };
};
